import React, { useRef, useState, useCallback, lazy, Suspense } from 'react';
import { Spin } from 'antd';
import { createUseStyles } from 'react-jss';
import { EditorToolbar } from './EditorToolbar';
import { CalculationFunctionPicker } from './CalculationFunctionPicker';
import { FieldReferenceAutocomplete } from './FieldReferenceAutocomplete';
import { CalculationFunction } from '../calculationMetadataTypes';
import 'ace-builds/src-noconflict/mode-pgsql';
import 'ace-builds/src-noconflict/theme-chrome';

const AceEditor = lazy(() => import('react-ace'));

const useStyles = createUseStyles({
    container: {
        position: 'relative',
    },
    editorWrapper: {
        '& .ace_editor': {
            borderRadius: '0 0 4px 4px',
            border: '1px solid #d9d9d9',
            borderTop: 'none',
        },
    },
    loadingContainer: {
        height: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #d9d9d9',
        borderRadius: '0 0 4px 4px',
        borderTop: 'none',
    },
});

export interface AvailableField {
    prettyName: string;
    type: string;
    source: 'backend' | 'custom';
}

interface EnhancedSqlEditorProps {
    value: string;
    onChange: (value: string) => void;
    availableFields: AvailableField[];
    calculationFunctions: CalculationFunction[];
    placeholder?: string;
    height?: string;
}

export const EnhancedSqlEditor: React.FC<EnhancedSqlEditorProps> = ({
    value,
    onChange,
    availableFields,
    calculationFunctions,
    placeholder = 'Enter SQL expression',
    height = '200px',
}) => {
    const classes = useStyles();
    const editorRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [showFunctionPicker, setShowFunctionPicker] = useState(false);
    const [showFieldPicker, setShowFieldPicker] = useState(false);
    const [showAutocomplete, setShowAutocomplete] = useState(false);
    const showAutocompleteRef = useRef(false);
    const [autocompletePosition, setAutocompletePosition] = useState({ x: 0, y: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [bracketStartColumn, setBracketStartColumn] = useState(0);

    const setShowAutocompleteWithRef = useCallback((value: boolean) => {
        showAutocompleteRef.current = value;
        setShowAutocomplete(value);
    }, []);

    const handleEditorChange = (newValue: string, event?: any) => {
        const editor = editorRef.current?.editor;
        if (!editor) {
            onChange(newValue);
            return;
        }

        // editor.getCursorPosition() returns the pre-edit cursor during onChange,
        // so derive the post-edit cursor from the delta event instead.
        const cursorPosition = event
            ? event.action === 'remove'
                ? event.start
                : event.end
            : editor.getCursorPosition();

        const lines = newValue.split('\n');
        const currentLine = lines[cursorPosition.row] || '';
        const textBeforeCursor = currentLine.substring(0, cursorPosition.column);
        const bracketMatch = textBeforeCursor.match(/\[\[([^\]]*?)$/);

        if (bracketMatch) {
            if (!showAutocompleteRef.current) {
                const screenPosition = editor.renderer.textToScreenCoordinates(
                    cursorPosition.row,
                    cursorPosition.column,
                );
                const containerRect = containerRef.current?.getBoundingClientRect();
                setAutocompletePosition({
                    x: screenPosition.pageX - (containerRect?.left || 0),
                    y: screenPosition.pageY - (containerRect?.top || 0) + 20,
                });
                setShowAutocompleteWithRef(true);
                setBracketStartColumn(cursorPosition.column);
            }
            setSearchQuery(bracketMatch[1]);
        } else if (showAutocompleteRef.current) {
            setShowAutocompleteWithRef(false);
        }

        onChange(newValue);
    };

    const handleInsertFunction = (template: string) => {
        const editor = editorRef.current?.editor;
        if (!editor) return;

        editor.insert(template);
        editor.focus();
        setShowFunctionPicker(false);
    };

    const handleInsertField = (fieldName: string) => {
        const editor = editorRef.current?.editor;
        if (!editor) return;

        if (showAutocomplete) {
            const cursorPosition = editor.getCursorPosition();
            const session = editor.getSession();
            const line = session.getLine(cursorPosition.row);

            const textBeforeCursor = line.substring(0, cursorPosition.column);
            const textAfterCursor = line.substring(cursorPosition.column);
            const bracketIndex = textBeforeCursor.lastIndexOf('[[');

            if (bracketIndex !== -1) {
                let endColumn = cursorPosition.column;

                // Check if there are closing brackets immediately after cursor
                if (textAfterCursor.startsWith(']]')) {
                    endColumn += 2;
                }

                const range = {
                    start: { row: cursorPosition.row, column: bracketIndex },
                    end: { row: cursorPosition.row, column: endColumn },
                };
                session.replace(range, `[[${fieldName}]]`);
            }
        } else {
            const toInsert = `[[${fieldName}]]`;
            editor.insert(toInsert);
        }

        editor.focus();
        setShowFieldPicker(false);
        setShowAutocompleteWithRef(false);
    };

    return (
        <div className={classes.container} ref={containerRef}>
            <EditorToolbar
                onInsertFunction={() => setShowFunctionPicker(!showFunctionPicker)}
                onInsertField={() => setShowFieldPicker(!showFieldPicker)}
            />

            <div className={classes.editorWrapper}>
                <Suspense
                    fallback={
                        <div className={classes.loadingContainer}>
                            <Spin />
                        </div>
                    }
                >
                    <AceEditor
                        ref={editorRef}
                        mode="pgsql"
                        theme="chrome"
                        value={value}
                        onChange={handleEditorChange}
                        name="custom-metric-sql-editor"
                        width="100%"
                        height={height}
                        placeholder={placeholder}
                        fontSize={14}
                        showPrintMargin={false}
                        showGutter={true}
                        highlightActiveLine={true}
                        setOptions={{
                            showLineNumbers: true,
                            tabSize: 2,
                        }}
                        style={{
                            zIndex: 0,
                        }}
                    />
                </Suspense>
            </div>

            {showFunctionPicker && (
                <CalculationFunctionPicker
                    functions={calculationFunctions}
                    onSelect={handleInsertFunction}
                    onClose={() => setShowFunctionPicker(false)}
                />
            )}

            {showFieldPicker && (
                <FieldReferenceAutocomplete
                    fields={availableFields}
                    onSelect={handleInsertField}
                    onClose={() => setShowFieldPicker(false)}
                    position={{ x: 0, y: 48 }}
                    isManual={true}
                />
            )}

            {showAutocomplete && (
                <FieldReferenceAutocomplete
                    key={searchQuery}
                    fields={availableFields}
                    searchQuery={searchQuery}
                    onSelect={handleInsertField}
                    onClose={() => setShowAutocompleteWithRef(false)}
                    position={autocompletePosition}
                />
            )}
        </div>
    );
};

export default EnhancedSqlEditor;

